import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;


interface ReminderModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        reminder_id?: number;
        reminder_ledger_id?: number;
        reminder_trial?: string;
        reminder_delivery?: string;
    } | null;
    isEditMode?: boolean;
}

const ReminderModel: React.FC<ReminderModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [ledgerList, setLedgerList] = useState<{ ledger_id: string, ledger_first_name: string, ledger_last_name: string }[]>([]);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    async function fetchTailorLedgers() {
        try {
            const response = await apiRequest({
                method: 'get',
                url: '/ledger-data?category=tailor'   // your API
            });

            console.log("Wallet Ledger Data:", response);

            setLedgerList(response.ledgers);

        } catch (err) {
            showError("Failed to load wallet ledger names");
        }
    }

    useEffect(() => {
        fetchTailorLedgers();
    }, []);

    const ledgerOptions = ledgerList
        .filter(
            (ledger) =>
                ledger.ledger_first_name?.trim() && ledger.ledger_last_name?.trim()
        )
        .map((ledger) => ({
            label: `${ledger.ledger_first_name} ${ledger.ledger_last_name}`, // shows in dropdown
            value: ledger.ledger_id, // stored in form as reminder_ledger_id
        }));



    // Handle form submit
    const handleAddReminder = async (values: any) => {
        try {
            const dataVal = {
                reminder_ledger_id: values.reminder_ledger_id,
                reminder_trial: values.reminder_trial,
                reminder_delivery: values.reminder_delivery,
                ...(initialValues?.reminder_id && { reminder_id: initialValues.reminder_id }),
            };
            const url = isEditMode
                ? 'edit-reminder'
                : 'add-reminder';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'reminder updated successfully!' : ' reminder name created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} reminder name`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                reminder_trial: initialValues.reminder_trial
                    ? dayjs(initialValues.reminder_trial)
                    : null,
                reminder_delivery: initialValues.reminder_delivery
                    ? dayjs(initialValues.reminder_delivery)
                    : null,
                reminder_ledger_id: initialValues.reminder_ledger_id,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Reminder' : 'Add New Reminder'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
            centered
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddReminder}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name={"reminder_trial"}
                            label="Date of Trail"
                            rules={[{ required: true, message: 'Please select Date of Trail' }]}
                        >
                            <DatePicker style={{ width: '100%', padding: "5px", background: "#f5f5f5", margin: "5px" }} className="simple-input-radius-with-border" format="DD-MM-YYYY" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name={"reminder_delivery"}
                            label="Date of Delivery"
                            rules={[{ required: true, message: 'Please select Date of Delivery' }]}
                        >
                            <DatePicker style={{ width: '100%', padding: "5px", background: "#f5f5f5", margin: "5px" }} className="simple-input-radius-with-border" format="DD-MM-YYYY" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="reminder_ledger_id" // store ledger_id here
                            label="Tailor Name"
                            rules={[{ required: true, message: 'Please select a tailor name' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Tailor Name"
                                options={ledgerOptions} // mapped options
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                style={{ width: '100%', borderRadius: 4 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Reminder
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ReminderModel;
